import { A } from '@adornis/base/env-info.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';

export interface StoredFile {
  id: string;
  name: string;
  type: string;
  size: number;
  data: ArrayBuffer;
  uploadedAt: Date;
  /** set for a document that already lives on the server - uploading it again would only create a copy */
  serverFileID?: string;
}

/** `attachment; filename="x"; filename*=UTF-8''…` - the encoded form carries the real name */
const fileNameFromDisposition = (disposition: string | null) => {
  const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/)?.[1];
  if (encoded) return decodeURIComponent(encoded);
  return disposition?.match(/filename="([^"]+)"/)?.[1] ?? null;
};

class IndexedDBFileStorage {
  private readonly dbName = 'SusFormFiles';
  private readonly dbVersion = 1;
  private readonly storeName = 'files';
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    // disabled as indexdb has no async comp and just uses callbacks
    // eslint-disable-next-line promise/avoid-new
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          store.createIndex('name', 'name', { unique: false });
          store.createIndex('uploadedAt', 'uploadedAt', { unique: false });
        }
      };
    });
  }

  async storeFile(file: File): Promise<string> {
    return this.put({
      id: A.getGloballyUniqueID(),
      name: file.name,
      type: file.type,
      size: file.size,
      data: await file.arrayBuffer(),
      uploadedAt: new Date(),
    });
  }

  /**
   * Keeps a document that already lives on the server, so a form can show and preview it like a picked file.
   * One that cannot be fetched is kept without content: its server reference must survive the submit.
   */
  async storeServerFile(serverFileID: string): Promise<string> {
    const response = await fetch(AdornisFile.getDownloadLink(serverFileID), { credentials: 'include' }).catch(
      () => null,
    );
    const data = response?.ok ? await response.blob().catch(() => null) : null;
    return this.put({
      id: A.getGloballyUniqueID(),
      name: fileNameFromDisposition(response?.headers.get('content-disposition') ?? null) ?? 'Gespeichertes Dokument',
      type: data?.type ?? '',
      size: data?.size ?? 0,
      data: data ? await data.arrayBuffer() : new ArrayBuffer(0),
      uploadedAt: new Date(),
      serverFileID,
    });
  }

  private async put(storedFile: StoredFile): Promise<string> {
    if (!this.db) await this.init();

    const id = storedFile.id;
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.add(storedFile);

      request.onsuccess = () => resolve(id);
      request.onerror = () => reject(request.error);
    });
  }

  async getFile(id: string): Promise<StoredFile | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async uploadFileToServer(id: string): Promise<string> {
    if (!this.db) await this.init();

    const file = await this.getFile(id);
    if (!file) throw new Error('File not found');
    if (file.serverFileID) return file.serverFileID;

    const fileData = file.data;
    const fileType = file.type || 'application/octet-stream';
    const fileName = file.name || 'file';
    const browserFile = new File([fileData], fileName, { type: fileType });
    const uploadedFileID = await AdornisFile.upload(browserFile);

    return uploadedFileID;
  }

  async deleteFile(id: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      request.onerror = () => reject(request.error);
    });
  }

  async getAllFiles(): Promise<StoredFile[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      request.onerror = () => reject(request.error);
    });
  }

  createFileUrl(storedFile: StoredFile): string {
    const blob = new Blob([storedFile.data], { type: storedFile.type });
    return URL.createObjectURL(blob);
  }
}

export const fileStorage = new IndexedDBFileStorage();
